import { Button } from '@neo/index'
import { Checkbox } from '@neo/primitives/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@neo/primitives/dialog'
import { Input } from '@neo/primitives/input'
import { Select } from '@neo/primitives/select'
import { Switch } from '@neo/primitives/switch'
import type { ApiResponse } from '@shared/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useFlowId } from 'v2/pages/flow/hooks/use-flow-id'
import { useFlowStore } from 'v2/pages/flow/store'
import { HANDOFF_CONTEXT_FIELDS } from 'v2/pages/settings/flows/queues/qnr.config'
import { resolveBaseUrl } from 'v2/shared/services/create-service'
import { handoffContextService } from 'v2/shared/services/handoff-context-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { conversationQueryKeys } from 'v2/shared/utils/query-key-management'
import type { HandoffContextSource } from 'v2/types/api/handoff-context'
import { errorToast, successToast } from 'v2/utils/toasts'
import { fieldLabel, integrationInstructions, uploadUrl } from './handoff-context.helpers'
import type { QNRFieldRendererProps, QNRFormValues } from './types'

type ContextPolicy = NonNullable<QNRFormValues['context']>
const DEFAULT_POLICY: ContextPolicy = {
  enabled: false,
  sources: ['external'],
  source_uuid: '',
  fields: [],
}

const adminError = (title: string, action: string) => (error: unknown) =>
  errorToast({
    title,
    description:
      (error as AxiosError).response?.status === 403
        ? `Only administrators can ${action}.`
        : getErrorText(error as AxiosError<ApiResponse<unknown>>),
  })

const copy = (text: string, what: string) =>
  navigator.clipboard.writeText(text).then(
    () => successToast({ title: `${what} copied` }),
    () => errorToast({ title: `${what} not copied`, description: 'Select and copy it manually.' }),
  )

/** Context section inside Assign to human: this node's policy plus the shared external-source setup.
 * A `basic` field gets no field subscription from FieldsRenderer, so the section subscribes itself. */
export const HandoffContext = ({ form }: Pick<QNRFieldRendererProps, 'form'>) => (
  <form.Subscribe selector={(state) => state.values.context}>
    {(context) => <ContextFields form={form} context={context || DEFAULT_POLICY} />}
  </form.Subscribe>
)

const ContextFields = ({
  form,
  context,
}: Pick<QNRFieldRendererProps, 'form'> & { context: ContextPolicy }) => {
  const isEditing = useFlowStore((state) => state.isEditing)
  const queryClient = useQueryClient()
  const {
    data: sources = [],
    isSuccess: sourcesLoaded,
    isFetching: sourcesFetching,
  } = useQuery({
    queryKey: conversationQueryKeys().handoffContextSources,
    queryFn: async () => (await handoffContextService.listSources()).data,
    enabled: context.enabled,
    retry: false,
    staleTime: 60_000,
  })
  const [newName, setNewName] = useState('')
  const [secret, setSecret] = useState<string | null>(null)
  const create = useMutation({
    mutationFn: async (name: string) => (await handoffContextService.createSource(name)).data,
    onSuccess: (source) => {
      setSecret(source.secret || null)
      setNewName('')
      form.setFieldValue('context.source_uuid', source.uuid)
      return queryClient.invalidateQueries({
        queryKey: conversationQueryKeys().handoffContextSources,
      })
    },
    onError: adminError('Source not created', 'create sources'),
  })
  const rotate = useMutation({
    mutationFn: async (uuid: string) => (await handoffContextService.rotateSource(uuid)).data,
    onSuccess: (source) => setSecret(source.secret || null),
    onError: adminError('Credential not rotated', 'rotate credentials'),
  })
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const revoke = useMutation({
    mutationFn: async (uuid: string) => (await handoffContextService.revokeSource(uuid)).data,
    onSuccess: () => {
      setConfirmRevoke(false)
      successToast({ title: 'Source revoked' })
      return queryClient.invalidateQueries({
        queryKey: conversationQueryKeys().handoffContextSources,
      })
    },
    onError: adminError('Source not revoked', 'revoke sources'),
  })
  const url = uploadUrl(resolveBaseUrl())
  const { id: flowId } = useFlowId()
  // The receiving binding is a live, admin, org-level setting stored on the source, separate from
  // this draft's node policy: it changes only through the explicit button below, never by saving
  // or picking a source, so an unpublished edit cannot move ingress for the published Flow.
  // Shared-setting mutations still require edit mode; historical/read-only views cannot rebind ingress.
  const bindFlow = useMutation({
    mutationFn: async (sourceUuid: string) =>
      (await handoffContextService.bindFlow(sourceUuid, flowId as string)).data,
    onSuccess: () => {
      successToast({ title: 'Flow bound to source' })
      return queryClient.invalidateQueries({
        queryKey: conversationQueryKeys().handoffContextSources,
      })
    },
    onError: adminError('Flow not bound to source', 'bind Flows'),
  })
  const boundSource = sources.find(
    (s: HandoffContextSource) =>
      !s.revoked_at && !!flowId && (s.receiving_flows || []).includes(flowId),
  )
  const selectedSource = sources.find(
    (s: HandoffContextSource) => !s.revoked_at && s.uuid === context.source_uuid,
  )
  // Reconcile a saved binding only after an authoritative source list has loaded.
  // Creation keeps its new selection while the source-list invalidation is in flight.
  useEffect(() => {
    if (
      sourcesLoaded &&
      !sourcesFetching &&
      !create.isPending &&
      context.source_uuid &&
      !selectedSource
    ) {
      form.setFieldValue('context.source_uuid', '')
    }
  }, [sourcesLoaded, sourcesFetching, create.isPending, context.source_uuid, selectedSource, form])
  // Display policy only: which sources/fields this node shows. The call's handoff id is a
  // voice receipt the conversation store records at ingress, never a value saved here.
  const update = (patch: Partial<ContextPolicy>) => {
    form.setFieldValue('context', { ...context, ...patch })
  }
  const toggleField = (field: (typeof HANDOFF_CONTEXT_FIELDS)[number], on: boolean) =>
    update({
      fields: on
        ? [...new Set([...context.fields, field])]
        : context.fields.filter((f) => f !== field),
    })

  return (
    <div className="flex flex-col gap-3" data-testid="qnr-context-section">
      <Dialog open={create.isPending || rotate.isPending || !!secret}>
        <DialogContent
          showCloseButton={false}
          // Override the house default so Radix can focus the one-time credential dialog.
          onOpenAutoFocus={() => {}}
          onEscapeKeyDown={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => event.preventDefault()}
        >
          <DialogTitle>{secret ? 'Save your source credential' : 'Issuing credential'}</DialogTitle>
          <DialogDescription>
            {secret
              ? 'Copy and save this credential before continuing. It is shown only once and cannot be retrieved later. Any previous credential of this source no longer works.'
              : 'Your source credential will appear here when it is issued.'}
          </DialogDescription>
          {secret && (
            <>
              <code className="break-all text-xs-400" data-testid="qnr-context-secret">
                {secret}
              </code>
              <Button type="button" variant="outline" onClick={() => copy(secret, 'Credential')}>
                Copy credential
              </Button>
              <Button type="button" onClick={() => setSecret(null)}>
                I have saved the credential
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
      <Switch
        id="context.enabled"
        name="context.enabled"
        value={context.enabled}
        onChange={(on) => update({ enabled: on, sources: ['external'] })}
        label="Include conversation context"
      />
      {context.enabled && (
        <>
          <div className="flex flex-col gap-1">
            <span className="text-xs-500 text-secondary">
              Information to show (all when none selected)
            </span>
            {HANDOFF_CONTEXT_FIELDS.map((field) => (
              <Checkbox
                key={field}
                id={`context.field.${field}`}
                name={`context.field.${field}`}
                label={fieldLabel(field)}
                value={context.fields.includes(field)}
                onChange={(on) => toggleField(field, on)}
              />
            ))}
          </div>
          <div
            className="flex flex-col gap-2 rounded-md border border-st2 bg-gray-50 p-3"
            data-testid="qnr-context-external"
          >
            <span className="text-xs-500">
              External integration setup (shared by every Flow of this organization)
            </span>
            <Select
              name="context.source_uuid"
              value={context.source_uuid || ''}
              placeholder="Choose a source"
              onChange={(value) => update({ source_uuid: value as string })}
              error={
                !context.source_uuid ? 'Choose an active source before publishing.' : undefined
              }
              options={sources.flatMap((source: HandoffContextSource) =>
                source.revoked_at ? [] : [{ value: source.uuid, label: source.name }],
              )}
            />
            {selectedSource && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!isEditing || rotate.isPending}
                  onClick={() => rotate.mutate(selectedSource.uuid)}
                >
                  Rotate credential
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!isEditing || revoke.isPending}
                  onClick={() =>
                    confirmRevoke ? revoke.mutate(selectedSource.uuid) : setConfirmRevoke(true)
                  }
                >
                  {confirmRevoke ? `Confirm: revoke ${selectedSource.name}` : 'Revoke source'}
                </Button>
              </div>
            )}
            <div
              className="flex flex-col gap-1 rounded-md bg-bg2 p-2"
              data-testid="qnr-context-binding"
            >
              <span className="text-xs-500">
                {boundSource
                  ? `Calls on this Flow are matched to: ${boundSource.name}`
                  : 'Calls on this Flow are not matched to any source yet'}
              </span>
              {flowId && selectedSource && selectedSource.uuid !== boundSource?.uuid && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!isEditing || bindFlow.isPending}
                  onClick={() => bindFlow.mutate(selectedSource.uuid)}
                >
                  Use {selectedSource.name} for calls on this Flow now
                </Button>
              )}
              {selectedSource && boundSource && selectedSource.uuid !== boundSource.uuid && (
                <span className="text-xs-400 text-destructive">
                  This node allows {selectedSource.name}, but calls are matched to{' '}
                  {boundSource.name}; publish this Flow and switch the binding together or context
                  will be unavailable.
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="New source name (e.g. Acme voice bot)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!isEditing || !newName.trim() || create.isPending}
                onClick={() => create.mutate(newName.trim())}
              >
                Create
              </Button>
            </div>
            <div className="text-xs-400 text-secondary break-all">Upload URL: {url}</div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => copy(integrationInstructions(url), 'Integration instructions')}
            >
              Copy integration instructions
            </Button>
          </div>
          <span className="text-xs-400 text-secondary">
            Calls route immediately; context that arrives later appears automatically.
          </span>
        </>
      )}
    </div>
  )
}
